import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import apiClient from '@/services/api';
import type { AlcoholFoodDrinkReport, AlcoholFoodDrinkRow } from '@/types/api';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

const HEADERS = ['Type', 'Count', 'Amount'];

/**
 * Alcohol / Food / Drink — three-row summary (Food, Soft Drink, Alcohol) with
 * item count and amount plus a grand-total row, matching the POS report. Alcohol
 * is classified by the synced alcoholic-drink flag (with a name-heuristic fallback).
 */
export default function AlcoholFoodDrinkReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<AlcoholFoodDrinkReport>({
    queryKey: ['alcohol-food-drink', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getAlcoholFoodDrinkReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const rows = data?.rows ?? [];
  const total = data?.total;
  const num = (n: number) => Number(n ?? 0).toLocaleString();
  const isEmpty = !total || Number(total?.amount ?? 0) === 0;

  const rowFor = (r: AlcoholFoodDrinkRow): (string | number)[] => [r.name, num(r.count), formatCurrency(r.amount)];
  const bodyRows: (string | number)[][] = [
    ...rows.map(rowFor),
    ...(total ? [rowFor(total)] : []),
  ];

  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const sections = (): ReportSection[] => [{ heading: 'Alcohol / Food / Drink', headers: HEADERS, rows: bodyRows }];

  return (
    <ReportFrame
      title="Alcohol / Food / Drink"
      description="Item count and amount split into Food, Soft Drink and Alcohol (matches the POS report)."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={isEmpty}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`alcohol-food-drink-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Alcohol / Food / Drink', meta(), [], sections()))}
      onExportPdf={() => data && printGenericReport('Alcohol / Food / Drink Report', meta(), [], sections())}
    >
      <div className="card"><div className="card-body">
        <SimpleTable headers={HEADERS} rows={bodyRows} />
      </div></div>
    </ReportFrame>
  );
}
